import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ExtensionStatus } from '../types'
import { buildAuthModalPath } from '../lib/utils'

// Map old AppScreen enum values to new routes
const screenToRoute: Record<string, string> = {
  LANDING: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  TRUST: '/trust',
  WORKSPACE: '/workspace',
  IMPORT: '/import',
  STUDIO: '/studio',
  CAPABILITY_SETTINGS: '/capability-settings',
  PRICING_ENGINE: '/pricing',
  SETTINGS_GENERAL: '/settings/general',
  SETTINGS_SECURITY: '/settings/security',
  SETTINGS_BILLING: '/settings/billing',
  SETTINGS_SUBSCRIPTION: '/settings/subscription',
  SETTINGS_CREDITS: '/settings/credits',
  SETTINGS_PREFERENCES: '/settings/preferences',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_INTEGRATIONS: '/settings/integrations',
  COPYRIGHT_DASHBOARD: '/copyright/dashboard',
  COPYRIGHT_LETTER: '/copyright/letter',
  COPYRIGHT_PSD: '/copyright/psd',
  COPYRIGHT_WATERMARK: '/copyright/watermark',
  COPYRIGHT_BLOCKCHAIN: '/copyright/blockchain',
  COPYRIGHT_DISPUTE: '/copyright/dispute',
}

export function useAppNavigation() {
  const navigate = useNavigate()

  // Compatible setScreen function for gradual migration
  const setScreen = (screen: string) => {
    const route = screenToRoute[screen]
    if (route) {
      navigate(route)
    } else {
      console.warn(`Unknown screen: ${screen}`)
      navigate('/workspace')
    }
  }

  return {
    navigate,
    setScreen,
    // Direct navigation helpers
    goToLogin: () => {
      if (typeof window !== 'undefined') {
        navigate(buildAuthModalPath(window.location.pathname, window.location.search, 'login'))
        return
      }
      navigate('/login')
    },
    goToSignup: () => {
      if (typeof window !== 'undefined') {
        navigate(buildAuthModalPath(window.location.pathname, window.location.search, 'signup'))
        return
      }
      navigate('/signup')
    },
    goToWorkspace: () => navigate('/workspace'),
    goToImport: () => navigate('/import'),
    goToStudio: () => navigate('/studio'),
    goToPricing: () => navigate('/pricing'),
    goToSettings: () => navigate('/settings'),
    goToCopyright: () => navigate('/copyright'),
  }
}

export function useExtensionStatus() {
  const [status, setStatus] = useState<ExtensionStatus>('NOT_FOUND')
  const [version, setVersion] = useState<string | null>(null)

  useEffect(() => {
    let detected = false

    const markConnected = (payload?: { version?: string }) => {
      detected = true
      setStatus('CONNECTED')
      if (payload?.version) {
        setVersion(payload.version)
      }
    }

    const detectFromDom = () => {
      const root = document.documentElement
      if (!root) return
      const extensionId = root.getAttribute('data-ozon-erp-extension-id')
      if (extensionId) {
        const extVersion = root.getAttribute('data-ozon-erp-extension-version')
        markConnected({ version: extVersion ?? undefined })
      }
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data
      if (!data || typeof data !== 'object') return

      if (data.type === 'OZON_ERP_EXTENSION_ID') {
        markConnected({ version: data.version })
      }

      if (data.type === 'OZON_ERP_EXTENSION_STATUS' && data.installed) {
        markConnected({ version: data.version })
      }
    }

    window.addEventListener('message', handleMessage)

    detectFromDom()
    window.postMessage({ type: 'OZON_ERP_REQUEST_STATUS' }, '*')

    const timer = window.setTimeout(() => {
      if (!detected) {
        setStatus('NOT_FOUND')
      }
    }, 1000)

    return () => {
      window.removeEventListener('message', handleMessage)
      window.clearTimeout(timer)
    }
  }, [])

  return { status, version, detected: status === 'CONNECTED' }
}
